import { humanoidFace, humanoidSkull } from './humanoid-head.js';
import type { HumanFace, HumanRigId, HumanSkull } from './human.js';
import { NOSE_SHAPE_DEFAULT, type NoseShape } from './humanoid-nose.js';

/** Halfling facial proportions in the shared skull coordinate space: round, open, large-eyed, with Human ears. */
export interface HalflingFaceMorph extends NoseShape {
  jawWidth: number; cheekWidth: number; cheek: number; hollow: number; brow: number;
  noseWidth: number; noseLength: number; noseProjection: number; noseRootHeight: number; noseBridge: number;
  mouthWidth: number; lipFullness: number; eyeSize: number; eyeDepth: number;
  /** Forward offset of the eye-level cross-section from the matching Human skull. */
  eyeLevelCentreOffset: number;
  /** Orbital plane before the Eye depth inset; the morph control shows the resulting skull plane. */
  eyePlane: number; eyeOpening: number; eyeTilt: number;
  earLength: number; earWidth: number;
}
export const HALFLING_FACE_DEFAULT: HalflingFaceMorph = {
  ...NOSE_SHAPE_DEFAULT, bridgeWidth: .92, wingWidth: 1.02,
  jawWidth: .95, cheekWidth: 1.04, cheek: .022, hollow: .002, brow: .002,
  noseWidth: .92, noseLength: .036, noseProjection: .094, noseRootHeight: 1.864, noseBridge: -.002,
  mouthWidth: .023, lipFullness: 1.04, eyeSize: 1.28, eyeLevelCentreOffset: 0, eyePlane: .0815, eyeDepth: .0052, eyeOpening: 1.02, eyeTilt: .04,
  earLength: .03, earWidth: .018,
};
export const HALFLING_FEMALE_FACE_DEFAULT: HalflingFaceMorph = {
  ...HALFLING_FACE_DEFAULT, tipTilt: 20, wingWidth: 1.1, wingFlare: .82, wingRaise: .0034, wingForward: .0032,
  nostrilSpacing: 1.29, nostrilHeight: .79, nostrilTilt: 2, nostrilRaise: -.0028, nostrilForward: -.0033,
  jawWidth: .96, cheekWidth: 1, cheek: .018, hollow: .006,
  noseWidth: .85, noseProjection: .099, noseRootHeight: 1.858, noseBridge: -.006,
  eyeLevelCentreOffset: -.012, eyePlane: .0805, eyeDepth: .003, eyeOpening: .76, eyeTilt: .18,
};
export const halflingFaces: { id: string; name: string; morph?: Partial<HalflingFaceMorph> }[] = [
  { id: 'standard', name: 'Halfling classic' },
  { id: 'round', name: 'Apple-cheeked', morph: { cheekWidth: 1.08, cheek: .026, jawWidth: .98, hollow: 0, lipFullness: 1.1 } },
  { id: 'button', name: 'Button nose', morph: { noseLength: .031, noseProjection: .09, noseWidth: .96, noseBridge: -.004 } },
  { id: 'sharp', name: 'Fox-faced', morph: { jawWidth: .9, cheekWidth: 1, hollow: .006, noseLength: .04, noseProjection: .099, eyeTilt: .09 } },
  { id: 'weathered', name: 'Weathered', morph: { brow: .005, eyeDepth: .0075, eyeOpening: .92, hollow: .008, cheek: .018 } },
  { id: 'wide-eyed', name: 'Wide-eyed', morph: { eyeSize: 1.34, eyeOpening: 1.06, brow: .001 } },
];
export function resolveHalflingFace(base: HalflingFaceMorph, id = 'standard'): HalflingFaceMorph {
  return { ...base, ...halflingFaces.find(face => face.id === id)?.morph };
}
export function halflingSkull(m: HalflingFaceMorph, body: HumanRigId): HumanSkull {
  const skull = humanoidSkull(body, 0.98 * m.jawWidth, 1.04 * m.cheekWidth);
  skull.jaw.centre[5] += m.eyeLevelCentreOffset;
  return {
    ...skull,
    eye: { x: .033, y: 1.859, plane: m.eyePlane - m.eyeDepth * .5, opening: m.eyeOpening },
    ear: { ...skull.ear, length: m.earLength, width: m.earWidth, thickness: .006 },
    mouth: { y: 1.795, width: m.mouthWidth, upper: 1, lower: 1, forward: 1, bow: 1.1, corners: .0016, groove: .0005, overlap: .0008, lift: .0008 },
    nose: { rootY: m.noseRootHeight, length: m.noseLength, tipZ: m.noseProjection,
      ...Object.fromEntries(Object.keys(NOSE_SHAPE_DEFAULT).map(key => [key, m[key as keyof NoseShape]])) },
  };
}
export function halflingFaceShape(m: HalflingFaceMorph, body: HumanRigId = 'human-male'): HumanFace {
  return { ...humanoidFace(body, { jaw: .96, chin: .9, square: .8 }, { chinDepth: 0 }), brow: m.brow, cheek: m.cheek, hollow: m.hollow, eye: m.eyeDepth, eyeSize: m.eyeSize,
    nose: m.noseWidth, lips: m.lipFullness, noseLength: 0, bridge: m.noseBridge, fullness: .002,
    eyeTilt: m.eyeTilt, fold: 0 };
}
